<script setup lang="ts">
    import { reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { getMonthlyReport, getMonthlyReportExportUrl, MonthlyReport } from './Billing.access';

    const prevMonth = new Date();
    prevMonth.setMonth(prevMonth.getMonth() - 1);

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    const state = reactive<{
        year: number;
        month: number;
        report: MonthlyReport | null;
        loading: boolean;
    }>({
        year: prevMonth.getFullYear(),
        month: prevMonth.getMonth() + 1,
        report: null,
        loading: false,
    });

    function getHeaders(rows: Record<string, unknown>[]): string[] {
        if (!rows || rows.length === 0) return [];
        return Object.keys(rows[0]);
    }

    async function preview(): Promise<void> {
        state.loading = true;
        try {
            state.report = await getMonthlyReport(state.year, state.month);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load monthly report' });
        } finally {
            state.loading = false;
        }
    }

    function download(): void {
        window.open(getMonthlyReportExportUrl(state.year, state.month), '_blank');
    }
</script>

<template>
    <div class="d-flex flex-wrap align-items-end row">
        <div class="col-md-2">
            <div class="form-group">
                <label>Year</label>
                <input
                    type="number"
                    class="form-control"
                    :value="state.year"
                    @change="state.year = Number(($event.target as HTMLInputElement).value)" />
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <label>Month</label>
                <select
                    :value="state.month"
                    class="form-control"
                    @change="state.month = Number(($event.target as HTMLSelectElement).value)">
                    <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
                </select>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <button type="button" class="btn btn-default" :disabled="state.loading" @click="preview">Preview</button>
            </div>
        </div>
        <div class="col-md-2">
            <div class="form-group">
                <button type="button" class="btn btn-default" @click="download">Download</button>
            </div>
        </div>
    </div>

    <div v-if="state.loading">Loading...</div>

    <div v-if="state.report">
        <hr />

        <template v-for="(section, key) in state.report" :key="key">
            <div v-if="Array.isArray(section) && section.length > 0">
                <h3>{{ key }}</h3>
                <table class="table">
                    <thead>
                        <tr>
                            <th v-for="header in getHeaders(section)" :key="header">{{ header }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in section" :key="i">
                            <td v-for="header in getHeaders(section)" :key="header">{{ row[header] }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    export default { name: 'BillingMonthlyReport' };
</script>

<style></style>
